import type { VehiclePastAssociationDto } from '@cobliteam/api-lib/src/__generated__/graphql';
import { useTranslation } from '@cobliteam/i18n';
import Typography from '@cobliteam/ui/components/Typography';
import Icon, { type IconNames } from '@cobliteam/ui/icons';
import {
  AssociationSource,
  sourceFromIdentificationMethod,
  useAssociationSourceLabels,
} from '@cobliteam/ui-business/components/MultiSelects/MultiSelectAssociationSource/MultiSelectAssociationSource';

interface AssociationSourceTextProps {
  association: VehiclePastAssociationDto;
}

const AssociationSourceText = ({ association }: AssociationSourceTextProps) => {
  const { t } = useTranslation('path-association');
  const associationLabels = useAssociationSourceLabels();

  const getAssociationTitleAndIcon = (assoc: VehiclePastAssociationDto) => {
    const source = assoc.identificationMethod ? sourceFromIdentificationMethod(assoc.identificationMethod) : undefined;

    switch (source) {
      case AssociationSource.FACE_ID:
        return {
          text: associationLabels[source],
          icon: 'scan-face',
        };
      case AssociationSource.MANUAL:
        return {
          text: assoc.associatedBy?.full_name || t('association_history.unidentified_user'),
          icon: 'user-pen',
        };
      case AssociationSource.APP:
        return {
          text: associationLabels[source],
          icon: 'smartphone',
        };
      case AssociationSource.IDENTIFIER:
        return {
          text: associationLabels[source],
          icon: 'credit-card',
        };
      case AssociationSource.DASHBOARD:
        return {
          text: associationLabels[source],
          icon: 'user',
        };
      default:
        return undefined;
    }
  };

  const titleAndIcon = getAssociationTitleAndIcon(association);

  if (!titleAndIcon) {
    return null;
  }

  return (
    <div className='flex items-center gap-2'>
      <div className='shrink-0 rounded-full bg-gray-2 p-2'>
        <Icon name={titleAndIcon.icon as IconNames} color='gray-dark' />
      </div>
      <div>
        <Typography variant='text-md' color='secondary'>
          {association.driver ? t('association_history.associated_by') : t('association_history.disassociated_by')}
        </Typography>
        <Typography variant='text-lg-semibold' className='line-clamp-1'>
          {titleAndIcon.text}
        </Typography>
      </div>
    </div>
  );
};

export default AssociationSourceText;
